import type { LineItemData, ProjectData } from './LineItemList';
import type { JobBreakdown } from '@jobos/shared';

// ============================================================================
// FlatItem — unified flat representation for DnD
// ============================================================================

export interface FlatItem {
  id: string;
  type: 'project-header' | 'line-item';
  /** null for project-headers and loose line items */
  projectId: string | null;
  data: ProjectData | LineItemData;
}

// ============================================================================
// Type guards
// ============================================================================

export function isFlatProject(item: FlatItem): item is FlatItem & { type: 'project-header' } {
  return item.type === 'project-header';
}

export function isFlatLineItem(item: FlatItem): item is FlatItem & { type: 'line-item' } {
  return item.type === 'line-item';
}

// Coerce an API-supplied numeric field to a number, defaulting to 0. The
// word-processor doc endpoints can return numeric columns as Postgres-flavoured
// strings; everything in editor state needs a real number for arithmetic and
// formatting (`.toFixed()`, totals, sortOrder math).
function toNum(value: unknown): number {
  if (typeof value === 'number') return value;
  if (value == null || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// ============================================================================
// toFlatItems
// ============================================================================

/**
 * Converts a JobBreakdown API response into a flat array of FlatItems.
 *
 * - Merges loose line items and projects into a single list, sorted by sortOrder.
 * - Projects are represented as a project-header followed by their member line items.
 * - API-only fields (jobId, createdAt, updatedAt) are stripped from project data.
 * - LineItemData.sortOrder defaults to 0 when undefined.
 */
export function toFlatItems(breakdown: JobBreakdown): FlatItem[] {
  type TopEntry =
    | { kind: 'loose'; sortOrder: number; item: LineItemData }
    | { kind: 'project'; sortOrder: number; project: ProjectData };

  const entries: TopEntry[] = [];

  // Loose line items
  for (const li of breakdown.lineItems) {
    const data: LineItemData = {
      id: li.id,
      category: li.category,
      description: li.description ?? '',
      totalCost: toNum(li.totalCost),
      subItems: li.subItems.map((si) => {
        if ('hours' in si && si.hours !== undefined && si.hours !== null) {
          return {
            id: si.id,
            type: si.type,
            // Coerce numerics — flag-on word-processor responses can deliver
            // these as strings (Postgres numeric → JSON string), and downstream
            // components like LaborRateSelect call .toFixed() on the value.
            hours: toNum(si.hours),
            rate: toNum((si as any).rate),
            rateType: (si as any).rateType ?? undefined,
            unitKey: (si as any).unitKey ?? undefined,
            note: si.note ?? undefined,
            markupRate: si.markupRate != null ? toNum(si.markupRate) : undefined,
          };
        }
        return {
          id: si.id,
          type: si.type,
          amount: toNum((si as any).amount),
          note: si.note ?? undefined,
          markupRate: si.markupRate != null ? toNum(si.markupRate) : undefined,
        };
      }),
      templateId: li.templateId ?? undefined,
      itemDate: li.itemDate ?? undefined,
      sortOrder: li.sortOrder ?? 0,
    };
    entries.push({ kind: 'loose', sortOrder: li.sortOrder ?? 0, item: data });
  }

  // Projects — strip API-only fields. Older snapshots may not include projects.
  for (const proj of breakdown.projects ?? []) {
    const projectData: ProjectData = {
      id: proj.id,
      name: proj.name,
      sortOrder: proj.sortOrder,
      lineItems: proj.lineItems.map((li) => ({
        id: li.id,
        category: li.category,
        description: li.description ?? '',
        totalCost: toNum(li.totalCost),
        subItems: li.subItems.map((si) => {
          if ('hours' in si && si.hours !== undefined && si.hours !== null) {
            return {
              id: si.id,
              type: si.type,
              hours: si.hours,
              rate: (si as any).rate ?? 0,
              rateType: (si as any).rateType ?? undefined,
              unitKey: (si as any).unitKey ?? undefined,
              note: si.note ?? undefined,
              markupRate: si.markupRate ?? undefined,
            };
          }
          return {
            id: si.id,
            type: si.type,
            amount: (si as any).amount ?? 0,
            note: si.note ?? undefined,
            markupRate: si.markupRate ?? undefined,
          };
        }),
        templateId: li.templateId ?? undefined,
        itemDate: li.itemDate ?? undefined,
        sortOrder: li.sortOrder ?? 0,
      })),
    };
    entries.push({ kind: 'project', sortOrder: proj.sortOrder, project: projectData });
  }

  // Sort by sortOrder (stable, ties preserved in original order)
  entries.sort((a, b) => a.sortOrder - b.sortOrder);

  // Flatten
  const flat: FlatItem[] = [];
  for (const entry of entries) {
    if (entry.kind === 'loose') {
      flat.push({
        id: entry.item.id,
        type: 'line-item',
        projectId: null,
        data: entry.item,
      });
    } else {
      // Project header
      flat.push({
        id: entry.project.id,
        type: 'project-header',
        projectId: null,
        data: entry.project,
      });
      // Project members
      for (const li of entry.project.lineItems) {
        flat.push({
          id: li.id,
          type: 'line-item',
          projectId: entry.project.id,
          data: li,
        });
      }
    }
  }

  return flat;
}

// ============================================================================
// fromFlatItems
// ============================================================================

/**
 * Reconstructs { looseItems, projects } from a flat FlatItem array.
 *
 * - Walks the array in order.
 * - Project members are grouped under the nearest preceding project header.
 * - sortOrder is assigned from array position (separate counters for top-level
 *   and within-project).
 */
export function fromFlatItems(items: FlatItem[]): { looseItems: LineItemData[]; projects: ProjectData[] } {
  const looseItems: LineItemData[] = [];
  const projects: ProjectData[] = [];

  let topLevelCounter = 0;
  let withinProjectCounter = 0;
  let currentProject: ProjectData | null = null;

  for (const item of items) {
    if (item.type === 'project-header') {
      // Finalise any previous project
      currentProject = { ...(item.data as ProjectData), sortOrder: topLevelCounter, lineItems: [] };
      projects.push(currentProject);
      topLevelCounter++;
      withinProjectCounter = 0;
    } else if (item.type === 'line-item') {
      const liData = item.data as LineItemData;
      if (item.projectId !== null && currentProject !== null && currentProject.id === item.projectId) {
        // Member of current project
        currentProject.lineItems.push({ ...liData, sortOrder: withinProjectCounter });
        withinProjectCounter++;
      } else {
        // Loose item
        looseItems.push({ ...liData, sortOrder: topLevelCounter });
        topLevelCounter++;
      }
    }
  }

  return { looseItems, projects };
}

// ============================================================================
// deriveSortOrders
// ============================================================================

/**
 * Returns a Map<id, sortOrder> based on position in the flat array.
 *
 * - Top-level counter increments for project-headers and loose line items.
 * - Within-project counter resets at each project-header and increments for members.
 * - Does NOT mutate the input array or its items.
 */
export function deriveSortOrders(items: FlatItem[]): Map<string, number> {
  const orders = new Map<string, number>();
  let topLevelCounter = 0;
  let withinProjectCounter = 0;
  let inProject = false;

  for (const item of items) {
    if (item.type === 'project-header') {
      orders.set(item.id, topLevelCounter);
      topLevelCounter++;
      withinProjectCounter = 0;
      inProject = true;
    } else if (item.type === 'line-item') {
      if (inProject && item.projectId !== null) {
        orders.set(item.id, withinProjectCounter);
        withinProjectCounter++;
      } else {
        orders.set(item.id, topLevelCounter);
        topLevelCounter++;
        inProject = false;
      }
    }
  }

  return orders;
}

// ============================================================================
// resolveProjectMembership
// ============================================================================

/**
 * Determines which project (if any) the dragged item should belong to,
 * based on what it is being dragged over.
 *
 * Rules:
 * - If dragged item is a project-header → no nesting, return null.
 * - If overItem is null → return null (dropped in void).
 * - If overItem is a project-header → return its id.
 * - If overItem is a project member (projectId != null) → return that projectId.
 * - If overItem is loose (line-item with projectId null) → return null.
 */
export function resolveProjectMembership(overItem: FlatItem | null, draggedItem: FlatItem): string | null {
  // Project headers cannot be nested inside other projects
  if (draggedItem.type === 'project-header') {
    return null;
  }

  if (overItem === null) {
    return null;
  }

  if (overItem.type === 'project-header') {
    return overItem.id;
  }

  // overItem is a line-item — return its projectId (may be null for loose)
  return overItem.projectId;
}
